import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

const resources = {
  en: {
    translation: {
      // Navigation
      nav: {
        home: 'Home',
        fleet: 'Our Fleet',
        fleetRental: 'Fleet Rental',
        longTerm: 'Long-Term',
        about: 'About',
        contact: 'Contact',
        admin: 'Admin',
        login: 'Login',
        logout: 'Logout',
      },
      // Hero Section - Tourist focused
      hero: {
        title: 'Car Rental Geneva',
        subtitle: 'Geneva Airport & City Center',
        description: 'Your trusted car rental partner in Geneva. Airport pickup, transparent pricing, and premium vehicles for tourists exploring Switzerland.',
        cta: 'View Our Fleet',
        searchTitle: 'Book Your Rental Car',
        trustBadge1: 'No Hidden Fees',
        trustBadge2: 'Airport Pickup',
        trustBadge3: 'EN/FR Support',
      },
      // Search Form
      search: {
        category: 'Vehicle Type',
        allCategories: 'All Vehicles',
        pickupLocation: 'Pickup Location',
        returnLocation: 'Return Location',
        pickupDate: 'Pickup Date',
        returnDate: 'Return Date',
        selectDates: 'Select dates',
        selectDateRange: 'Select pickup & return dates',
        searchBtn: 'Search Available Cars',
        searching: 'Searching...',
        nights: 'days',
      },
      // Categories
      categories: {
        suv: 'SUV',
        citadine: 'City Car',
        utilitaire: 'Van',
        premium: 'Premium',
      },
      // Vehicle
      vehicle: {
        from: 'From',
        perDay: '/day',
        seats: 'seats',
        doors: 'doors',
        viewDetails: 'View Details',
        bookNow: 'Book Now',
        features: 'Features',
        specifications: 'Specifications',
        transmission: 'Transmission',
        fuelType: 'Fuel Type',
        automatic: 'Automatic',
        manual: 'Manual',
        petrol: 'Petrol',
        diesel: 'Diesel',
        electric: 'Electric',
        hybrid: 'Hybrid',
        deposit: 'Security Deposit',
        depositNote: 'Pre-authorized only, not charged',
      },
      // Booking
      booking: {
        addOns: 'Add-ons & Options',
        summary: 'Booking Summary',
        days: 'days',
        dailyRate: 'Daily Rate',
        total: 'Total',
        deposit: 'Security Deposit',
        depositInfo: 'Pre-authorized on your card, released after return',
        continue: 'Continue to Checkout',
        checkout: 'Checkout',
        customerInfo: 'Your Details',
        pickupReturnTime: 'Pickup & Return Time',
        pickupTime: 'Pickup Time',
        returnTime: 'Return Time',
        fullName: 'Full Name',
        email: 'Email Address',
        phone: 'Phone Number',
        street: 'Street & Number',
        postalCode: 'Postal Code',
        city: 'City',
        country: 'Country',
        address: 'Address',
        dateOfBirth: 'Date of Birth',
        documents: 'Required Documents',
        idDocument: 'Government ID / Passport',
        licenseDocument: 'Driver\'s License',
        uploadDocument: 'Upload Document',
        payment: 'Secure Payment',
        paymentDescription: 'Pay rental amount now. Security deposit will be pre-authorized on your card (not charged) and released automatically after vehicle return.',
        confirmBooking: 'Confirm & Pay',
        processing: 'Processing...',
        success: 'Booking Confirmed!',
        successMessage: 'Your rental car is reserved. Check your email for confirmation and rental details.',
        bookingId: 'Booking Reference',
        viewBooking: 'View Booking Details',
        backToHome: 'Back to Home',
        fillAllFields: 'Please fill in all required fields',
        uploadDocuments: 'Please upload both ID and driver\'s license',
        downloadDocuments: 'Your Documents',
        downloadContract: 'Download Contract',
        downloadInvoice: 'Download Invoice',
      },
      // Trust & Conversion
      trust: {
        securePayment: 'Secure Payment',
        securePaymentDesc: 'SSL encrypted, 3D Secure enabled',
        insuredVehicles: 'Fully Insured',
        insuredVehiclesDesc: 'Comprehensive insurance included',
        noHiddenFees: 'No Hidden Fees',
        noHiddenFeesDesc: 'Transparent pricing, no surprises',
        airportDelivery: 'Airport Pickup',
        airportDeliveryDesc: 'Geneva Airport GVA service',
        support247: '24/7 Support',
        support247Desc: 'English & French assistance',
        depositOnly: 'Deposit Only Blocked',
        depositOnlyDesc: 'Not charged, auto-released',
      },
      // FAQ
      faq: {
        title: 'Frequently Asked Questions',
        q1: 'What is the minimum age to rent a vehicle?',
        a1: 'The minimum age is 18 years old. For drivers between 18 and 21, an additional deposit of CHF 500.– is required.',
        q2: 'What is the deposit amount?',
        a2: 'The standard deposit is CHF 1,000.–. For drivers aged 18 to 21: CHF 1,500.–. The deposit is pre-authorized or charged via credit card.',
        q3: 'How many kilometers are included?',
        a3: 'Each rental includes 300 km per day. Additional kilometers are charged according to the rate indicated at the time of booking.',
        q4: 'What is the fuel policy?',
        a4: 'Full/Full policy. The vehicle must be returned with the same fuel level. Otherwise: CHF 2.50/liter (excl. VAT) will be charged.',
        q5: 'Can I leave Switzerland with the vehicle?',
        a5: 'Yes, in Switzerland and neighboring countries (France, Germany, Italy, Austria). Any other destination requires written authorization.',
        q6: 'What happens in case of damage?',
        a6: 'Scratch < 5 cm: CHF 300.– per panel. Rim scratch: CHF 300.– per rim. Greater damages: CHF 180.–/hour (up to the excess limit).',
        q7: 'What if the vehicle is returned dirty?',
        a7: 'A flat fee of CHF 100.– applies in case of excessive dirt.',
        q8: 'What about late returns?',
        a8: '1 hour tolerance. Then CHF 50.–, followed by CHF 50.– per additional hour.',
        q9: 'When do I receive the contract?',
        a9: 'After signing the delivery report, the final contract is sent by email.',
        q10: 'What about after-hours return?',
        a10: 'The inspection may be carried out without the customer present. The findings will be binding.',
        q11: 'What to do in case of an accident?',
        a11: '1. Call police if necessary (117). 2. Fill out the accident report. 3. Take photos. 4. Call assistance if needed. 5. Immediately inform the agency.',
        q12: 'What is the cancellation policy?',
        a12: 'Free cancellation up to 48h before pickup. Less than 48h: minimum 1 day charged. Less than 24h: up to 2 days charged. Delay over 2h without contact: reservation cancelled and up to 2 days charged.',
        vehicleNote: 'Note: Some vehicles may require a higher minimum age, longer license tenure, or higher deposit. This information is indicated in the vehicle description.',
      },
      // Cancellation Policy
      cancellation: {
        title: 'Cancellation Policy',
        free: 'Free cancellation up to 48 hours before pickup',
        partial: 'Cancellation within 48h: One day rental fee applies',
        noShow: 'No-show: Full rental amount charged',
      },
      // Fleet Rental
      fleetRental: {
        title: 'Fleet Rental Solutions',
        subtitle: 'For Businesses',
        description: 'Tailored vehicle solutions for your business needs. From corporate fleets to event transportation, we provide flexible rental programs with dedicated support.',
        benefits: {
          title: 'Why Choose Our Fleet Services?',
          cost: 'Cost-Effective Solutions',
          costDesc: 'Reduce your transportation costs with our competitive fleet pricing.',
          flexibility: 'Flexible Terms',
          flexibilityDesc: 'Short-term or long-term contracts adapted to your needs.',
          support: 'Dedicated Support',
          supportDesc: '24/7 customer service and maintenance support.',
          variety: 'Wide Selection',
          varietyDesc: 'Choose from our diverse range of vehicles for any purpose.',
        },
        pricing: {
          title: 'Fleet Discounts',
          description: 'The more vehicles you rent, the more you save. Contact us for custom quotes on large fleets.',
          vehicles: 'Vehicles',
          discount: 'discount',
        },
        contact: {
          title: 'Request a Quote',
          description: 'Fill out the form and our fleet team will get back to you within 24 hours with a customized quote.',
          name: 'Name',
          namePlaceholder: 'Your name',
          company: 'Company Name',
          companyPlaceholder: 'Company name',
          email: 'Email',
          phone: 'Phone',
          vehicles: 'Number of Vehicles',
          duration: 'Rental Duration',
          message: 'Additional Details',
          messagePlaceholder: 'Tell us about your fleet needs...',
          select: 'Select',
          months: 'months',
          submit: 'Submit Request',
          submitting: 'Submitting...',
          benefit1: 'Dedicated account manager',
          benefit2: 'Flexible contract terms',
          benefit3: 'Priority maintenance support',
          benefit4: 'Replacement vehicle guarantee',
        },
      },
      // Long-Term Rental
      longTerm: {
        title: 'Long-Term Rental',
        subtitle: 'Extended Flexibility',
        description: 'Enjoy the benefits of having your own vehicle without the commitment of ownership. Our long-term rental programs offer competitive rates for rentals of 30 days or more.',
        benefits: {
          title: 'Long-Term Benefits',
          savings: 'Monthly Savings',
          savingsDesc: 'Up to 40% discount on daily rates for monthly rentals.',
          maintenance: 'Included Maintenance',
          maintenanceDesc: 'Regular maintenance and service included.',
          insurance: 'Full Insurance',
          insuranceDesc: 'Comprehensive insurance coverage included.',
          swap: 'Vehicle Swap',
          swapDesc: 'Option to swap vehicles during your rental period.',
        },
        pricing: {
          title: 'Long-Term Pricing',
          monthly: '/month',
          starting: 'Starting from',
        },
        contact: {
          title: 'Get a Quote',
          duration: 'Desired Duration',
          vehicleType: 'Preferred Vehicle Type',
          submit: 'Request Quote',
        },
      },
      // Footer
      footer: {
        description: 'Your trusted car rental in Geneva. Airport pickup, transparent pricing, and premium vehicles for tourists visiting Switzerland.',
        quickLinks: 'Quick Links',
        services: 'Vehicle Types',
        contact: 'Contact Us',
        rights: 'All rights reserved.',
        privacy: 'Privacy Policy',
        terms: 'Terms of Service',
      },
      // Admin
      admin: {
        dashboard: 'Dashboard',
        vehicles: 'Vehicles',
        bookings: 'Bookings',
        customers: 'Customers',
        calendar: 'Calendar',
        inquiries: 'Inquiries',
        reports: 'Reports',
        settings: 'Settings',
        totalVehicles: 'Total Vehicles',
        availableVehicles: 'Available',
        totalBookings: 'Total Bookings',
        pendingBookings: 'Pending',
        totalRevenue: 'Total Revenue',
        newInquiries: 'Inquiries',
        addVehicle: 'Add Vehicle',
        editVehicle: 'Edit Vehicle',
        deleteVehicle: 'Delete Vehicle',
        viewDetails: 'View Details',
        updateStatus: 'Update Status',
        deliveryReport: 'Delivery Report',
        returnReport: 'Return Report',
        generatePdf: 'Generate PDF',
      },
      // Common
      common: {
        loading: 'Loading...',
        error: 'An error occurred',
        success: 'Success!',
        cancel: 'Cancel',
        save: 'Save',
        delete: 'Delete',
        edit: 'Edit',
        view: 'View',
        close: 'Close',
        submit: 'Submit',
        back: 'Back',
        next: 'Next',
        yes: 'Yes',
        no: 'No',
        chf: 'CHF',
      },
    },
  },
  fr: {
    translation: {
      // Navigation
      nav: {
        home: 'Accueil',
        fleet: 'Nos Véhicules',
        fleetRental: 'Location Flotte',
        longTerm: 'Longue Durée',
        about: 'À Propos',
        contact: 'Contact',
        admin: 'Admin',
        login: 'Connexion',
        logout: 'Déconnexion',
      },
      // Hero Section - Tourist focused
      hero: {
        title: 'Location Voiture Genève',
        subtitle: 'Aéroport & Centre-Ville',
        description: 'Votre partenaire de confiance pour la location de voitures à Genève. Service aéroport, tarifs transparents et véhicules premium pour explorer la Suisse.',
        cta: 'Voir Nos Véhicules',
        searchTitle: 'Réservez Votre Voiture',
        trustBadge1: 'Sans Frais Cachés',
        trustBadge2: 'Service Aéroport',
        trustBadge3: 'Support FR/EN',
      },
      // Search Form
      search: {
        category: 'Type de Véhicule',
        allCategories: 'Tous les Véhicules',
        pickupLocation: 'Lieu de Prise en Charge',
        returnLocation: 'Lieu de Retour',
        pickupDate: 'Date de Départ',
        returnDate: 'Date de Retour',
        selectDates: 'Sélectionner les dates',
        selectDateRange: 'Choisir dates de départ & retour',
        searchBtn: 'Rechercher',
        searching: 'Recherche...',
        nights: 'jours',
      },
      // Categories
      categories: {
        suv: 'SUV',
        citadine: 'Citadine',
        utilitaire: 'Utilitaire',
        premium: 'Premium',
      },
      // Vehicle
      vehicle: {
        from: 'À partir de',
        perDay: '/jour',
        seats: 'places',
        doors: 'portes',
        viewDetails: 'Voir les Détails',
        bookNow: 'Réserver',
        features: 'Équipements',
        specifications: 'Caractéristiques',
        transmission: 'Transmission',
        fuelType: 'Carburant',
        automatic: 'Automatique',
        manual: 'Manuelle',
        petrol: 'Essence',
        diesel: 'Diesel',
        electric: 'Électrique',
        hybrid: 'Hybride',
        deposit: 'Caution',
        depositNote: 'Pré-autorisée uniquement, non débitée',
      },
      // Booking
      booking: {
        addOns: 'Options Supplémentaires',
        summary: 'Récapitulatif',
        days: 'jours',
        dailyRate: 'Tarif Journalier',
        total: 'Total',
        deposit: 'Caution',
        depositInfo: 'Pré-autorisée sur votre carte, libérée après retour',
        continue: 'Continuer',
        checkout: 'Paiement',
        customerInfo: 'Vos Informations',
        pickupReturnTime: 'Heure de Prise en Charge et Retour',
        pickupTime: 'Heure de Prise en Charge',
        returnTime: 'Heure de Retour',
        fullName: 'Nom Complet',
        email: 'Adresse Email',
        phone: 'Téléphone',
        street: 'Rue et Numéro',
        postalCode: 'Code Postal',
        city: 'Ville',
        country: 'Pays',
        address: 'Adresse',
        dateOfBirth: 'Date de Naissance',
        documents: 'Documents Requis',
        idDocument: 'Pièce d\'Identité / Passeport',
        licenseDocument: 'Permis de Conduire',
        uploadDocument: 'Télécharger',
        payment: 'Paiement Sécurisé',
        paymentDescription: 'Payez le montant de la location maintenant. La caution sera pré-autorisée (non débitée) et libérée automatiquement après le retour du véhicule.',
        confirmBooking: 'Confirmer et Payer',
        processing: 'Traitement...',
        success: 'Réservation Confirmée!',
        successMessage: 'Votre voiture de location est réservée. Consultez vos emails pour la confirmation et les détails.',
        bookingId: 'Référence de Réservation',
        viewBooking: 'Voir les Détails',
        backToHome: 'Retour à l\'Accueil',
        fillAllFields: 'Veuillez remplir tous les champs obligatoires',
        uploadDocuments: 'Veuillez télécharger la pièce d\'identité et le permis de conduire',
        downloadDocuments: 'Vos Documents',
        downloadContract: 'Télécharger le Contrat',
        downloadInvoice: 'Télécharger la Facture',
      },
      // Trust & Conversion
      trust: {
        securePayment: 'Paiement Sécurisé',
        securePaymentDesc: 'Cryptage SSL, 3D Secure activé',
        insuredVehicles: 'Véhicules Assurés',
        insuredVehiclesDesc: 'Assurance tous risques incluse',
        noHiddenFees: 'Sans Frais Cachés',
        noHiddenFeesDesc: 'Tarifs transparents, sans surprise',
        airportDelivery: 'Service Aéroport',
        airportDeliveryDesc: 'Aéroport de Genève GVA',
        support247: 'Support 24h/24',
        support247Desc: 'Assistance français & anglais',
        depositOnly: 'Caution Non Débitée',
        depositOnlyDesc: 'Bloquée puis libérée automatiquement',
      },
      // FAQ
      faq: {
        title: 'Questions Fréquentes',
        q1: 'Quel est l\'âge minimum pour louer un véhicule ?',
        a1: 'L\'âge minimum est 18 ans révolus. Pour les conducteurs entre 18 et 21 ans, une caution supplémentaire de CHF 500.– est demandée.',
        q2: 'Quel est le montant de la caution ?',
        a2: 'La caution standard est de CHF 1\'000.–. Pour les conducteurs de 18 à 21 ans : CHF 1\'500.–. La caution est préautorisée ou débitée via carte bancaire.',
        q3: 'Combien de kilomètres sont inclus ?',
        a3: 'Chaque location comprend 300 km par jour. Les kilomètres supplémentaires sont facturés selon le tarif indiqué lors de la réservation.',
        q4: 'Quelle est la politique carburant ?',
        a4: 'Politique Plein / Plein. Le véhicule doit être restitué avec le même niveau de carburant. À défaut : CHF 2.50/litre HT sera facturé.',
        q5: 'Puis-je sortir de Suisse avec le véhicule ?',
        a5: 'Oui, en Suisse et pays limitrophes (France, Allemagne, Italie, Autriche). Toute autre destination nécessite autorisation écrite.',
        q6: 'Que se passe-t-il en cas de dommage ?',
        a6: 'Rayure < 5 cm : CHF 300.– par élément. Rayure jante : CHF 300.– par jante. Dommages plus importants : CHF 180.–/heure (dans la limite de la franchise).',
        q7: 'Véhicule rendu sale ?',
        a7: 'Forfait CHF 100.– en cas de salissure excessive.',
        q8: 'Retard ?',
        a8: '1 heure de tolérance. Ensuite CHF 50.–, puis CHF 50.–/heure supplémentaire.',
        q9: 'Quand reçois-je le contrat ?',
        a9: 'Après signature du procès-verbal de livraison, le contrat final est envoyé par e-mail.',
        q10: 'Retour hors horaires ?',
        a10: 'Le contrôle peut être effectué sans la présence du client. Les constatations font foi.',
        q11: 'Que faire en cas d\'accident ?',
        a11: '1. Appeler la police si nécessaire (117). 2. Remplir le constat amiable. 3. Prendre des photos. 4. Appeler l\'assistance si besoin. 5. Informer immédiatement l\'agence.',
        q12: 'Politique d\'annulation',
        a12: 'Annulation gratuite jusqu\'à 48h avant la prise du véhicule. Moins de 48h : facturation d\'1 jour minimum. Moins de 24h : facturation jusqu\'à 2 jours. Retard supérieur à 2h sans contact : réservation annulée et facturation jusqu\'à 2 jours.',
        vehicleNote: 'Note : Certains véhicules peuvent requérir un âge minimum supérieur, une ancienneté de permis plus longue, ou une caution plus élevée. Ces informations sont stipulées dans la description du véhicule.',
      },
      // Cancellation Policy
      cancellation: {
        title: 'Politique d\'Annulation',
        free: 'Annulation gratuite jusqu\'à 48h avant la prise en charge',
        partial: 'Annulation sous 48h: Frais d\'une journée de location',
        noShow: 'Non-présentation: Montant total facturé',
      },
      // Fleet Rental
      fleetRental: {
        title: 'Solutions de Location de Flotte',
        subtitle: 'Pour les Entreprises',
        description: 'Solutions de véhicules sur mesure pour vos besoins professionnels. Des flottes d\'entreprise au transport événementiel, nous proposons des programmes flexibles avec un support dédié.',
        benefits: {
          title: 'Pourquoi Choisir Nos Services de Flotte?',
          cost: 'Solutions Économiques',
          costDesc: 'Réduisez vos coûts de transport avec nos tarifs compétitifs.',
          flexibility: 'Conditions Flexibles',
          flexibilityDesc: 'Contrats court ou long terme adaptés à vos besoins.',
          support: 'Support Dédié',
          supportDesc: 'Service client et maintenance 24h/24.',
          variety: 'Large Sélection',
          varietyDesc: 'Choisissez parmi notre gamme diversifiée de véhicules.',
        },
        pricing: {
          title: 'Remises Flotte',
          description: 'Plus vous louez de véhicules, plus vous économisez. Contactez-nous pour des devis personnalisés pour grandes flottes.',
          vehicles: 'Véhicules',
          discount: 'de remise',
        },
        contact: {
          title: 'Demander un Devis',
          description: 'Remplissez le formulaire et notre équipe flotte vous répondra dans les 24 heures avec un devis personnalisé.',
          name: 'Nom',
          namePlaceholder: 'Votre nom',
          company: 'Nom de l\'Entreprise',
          companyPlaceholder: 'Nom de l\'entreprise',
          email: 'Email',
          phone: 'Téléphone',
          vehicles: 'Nombre de Véhicules',
          duration: 'Durée de Location',
          message: 'Détails Supplémentaires',
          messagePlaceholder: 'Décrivez-nous vos besoins en flotte...',
          select: 'Sélectionner',
          months: 'mois',
          submit: 'Envoyer la Demande',
          submitting: 'Envoi en cours...',
          benefit1: 'Gestionnaire de compte dédié',
          benefit2: 'Conditions de contrat flexibles',
          benefit3: 'Support maintenance prioritaire',
          benefit4: 'Garantie véhicule de remplacement',
        },
      },
      // Long-Term Rental
      longTerm: {
        title: 'Location Longue Durée',
        subtitle: 'Flexibilité Étendue',
        description: 'Profitez des avantages d\'avoir votre propre véhicule sans l\'engagement de la propriété. Nos programmes de location longue durée offrent des tarifs compétitifs pour les locations de 30 jours ou plus.',
        benefits: {
          title: 'Avantages Longue Durée',
          savings: 'Économies Mensuelles',
          savingsDesc: 'Jusqu\'à 40% de réduction sur les tarifs journaliers.',
          maintenance: 'Entretien Inclus',
          maintenanceDesc: 'Maintenance et service réguliers inclus.',
          insurance: 'Assurance Complète',
          insuranceDesc: 'Couverture d\'assurance tous risques incluse.',
          swap: 'Échange de Véhicule',
          swapDesc: 'Option d\'échange de véhicule pendant votre période de location.',
        },
        pricing: {
          title: 'Tarifs Longue Durée',
          monthly: '/mois',
          starting: 'À partir de',
        },
        contact: {
          title: 'Obtenir un Devis',
          duration: 'Durée Souhaitée',
          vehicleType: 'Type de Véhicule Préféré',
          submit: 'Demander un Devis',
        },
      },
      // Footer
      footer: {
        description: 'Location de voitures de confiance à Genève. Service aéroport, tarifs transparents et véhicules premium pour les touristes visitant la Suisse.',
        quickLinks: 'Liens Rapides',
        services: 'Types de Véhicules',
        contact: 'Nous Contacter',
        rights: 'Tous droits réservés.',
        privacy: 'Politique de Confidentialité',
        terms: 'Conditions d\'Utilisation',
      },
      // Admin
      admin: {
        dashboard: 'Tableau de Bord',
        vehicles: 'Véhicules',
        bookings: 'Réservations',
        customers: 'Clients',
        calendar: 'Calendrier',
        inquiries: 'Demandes',
        reports: 'Rapports',
        settings: 'Paramètres',
        totalVehicles: 'Total Véhicules',
        availableVehicles: 'Disponibles',
        totalBookings: 'Total Réservations',
        pendingBookings: 'En Attente',
        totalRevenue: 'Revenu Total',
        newInquiries: 'Demandes',
        addVehicle: 'Ajouter un Véhicule',
        editVehicle: 'Modifier le Véhicule',
        deleteVehicle: 'Supprimer le Véhicule',
        viewDetails: 'Voir les Détails',
        updateStatus: 'Mettre à Jour le Statut',
        deliveryReport: 'Rapport de Livraison',
        returnReport: 'Rapport de Retour',
        generatePdf: 'Générer PDF',
      },
      // Common
      common: {
        loading: 'Chargement...',
        error: 'Une erreur est survenue',
        success: 'Succès!',
        cancel: 'Annuler',
        save: 'Enregistrer',
        delete: 'Supprimer',
        edit: 'Modifier',
        view: 'Voir',
        close: 'Fermer',
        submit: 'Soumettre',
        back: 'Retour',
        next: 'Suivant',
        yes: 'Oui',
        no: 'Non',
        chf: 'CHF',
      },
    },
  },
};

i18n
  .use(initReactI18next)
  .init({
    resources,
    lng: 'en',
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false,
    },
  });

export default i18n;
